/**
 * Malka Terp as the remote control of an outside meeting: a client's own
 * Zoom, Google Meet, Microsoft Teams or Webex meeting
 * (docs/outside-meeting-desk.md). The interpreter accepts on the phone. The
 * meeting opens on their web console on their computer (the desk). The phone
 * reports the steps that the call record needs:
 *
 *   1. "I'm in the meeting" (entered): compensable time starts.
 *   2. "<Client> identified" (VRS only): in 5 minutes, or the TRS Fund does
 *      not pay for the call.
 *   3. "End interpreting" with the reason (ended).
 *
 * The phone never gets the meeting link. This file has the state and the
 * words only. The queue middleware keeps the state, and
 * InterpreterMeetingCallCard shows it on Home.
 */
import { PLATFORMS, PLATFORM_LABELS } from '../../../shared/external-meeting-links';
/** After "I'm in the meeting", a VRS interpreter has 5 minutes to identify the client. */
export const IDENTIFY_WINDOW_MS = 5 * 60 * 1000;
/** A sent step with no answer after this time can be sent again. */
export const MEETING_STEP_TIMEOUT_MS = 12000;
/**
 * The 47 CFR 64.604(e)(4) end conditions. The values and the words are the
 * same as on the web console (interpreter-external-meeting.js END_REASONS).
 */
export const MEETING_END_REASONS = Object.freeze([
    { value: 'meeting_ended', label: 'The meeting ended' },
    { value: 'hearing_left', label: 'All hearing participants left' },
    { value: 'signers_left', label: 'All signing participants left' },
    { value: 'requester_left_5min', label: 'The client left and 5 minutes passed' },
    { value: 'not_needed', label: 'Interpreting was not needed' },
    { value: 'interpreter_left', label: 'I have to leave' }
]);
export const MEETING_DESK_REQUIRED_TEXT = 'Open the web console to take this meeting';
export const MEETING_DESK_MISSING_TEXT = 'Your web console is not connected. Open it on your computer.';
export const MEETING_STEP_NO_ANSWER_TEXT = 'No answer from the server yet. Try again.';
function text(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
function platformValue(value) {
    return PLATFORMS.includes(value) ? value : null;
}
function isoOrNull(value) {
    const iso = text(value);
    return iso && Number.isFinite(Date.parse(iso)) ? iso : null;
}
/** An absent field keeps the known value. A null field clears it. */
function isoField(value, known) {
    return value === undefined ? known : isoOrNull(value);
}
function stepRecorded(call, event) {
    return (event === 'entered' && Boolean(call.enteredAt)) || (event === 'identified' && Boolean(call.identifiedAt));
}
/** "Zoom meeting"; "Outside meeting" when the platform is not known. */
export function meetingLabel(platform) {
    return `${platform ? PLATFORM_LABELS[platform] : 'Outside'} meeting`;
}
/** The desk from meeting_desk_status; undefined for a message that is not readable. */
export function meetingDeskFromStatus(data) {
    if (!data || typeof data.connected !== 'boolean') {
        return undefined;
    }
    return { connected: data.connected, device: data.connected ? text(data.device) || null : null };
}
/**
 * The call from request_accepted, before its external_meeting_call arrives.
 * undefined when the accept names no call.
 */
export function meetingCallFromAccept(data, deskDevice, now) {
    const callId = text(data.callId);
    if (!callId) {
        return undefined;
    }
    return {
        callId,
        callType: data.callType === 'vri' ? 'vri' : 'vrs',
        clientName: text(data.clientName) || 'Client',
        deskDevice: text(deskDevice) || null,
        enteredAt: null,
        host: text(data.externalMeeting?.host) || null,
        identifiedAt: null,
        identifiedInTime: false,
        identifyBy: null,
        knownAt: now,
        platform: platformValue(data.externalMeeting?.platform)
    };
}
/**
 * The call from external_meeting_call (after the accept, or at sign-in during
 * the call). A step that this phone waits on stays pending until the server
 * records it.
 */
export function meetingCallFromSnapshot(data, previous, now) {
    const same = previous?.callId === data.callId ? previous : undefined;
    const call = {
        callId: data.callId,
        callType: data.callType === 'vri' ? 'vri' : data.callType === 'vrs' ? 'vrs' : same?.callType || 'vrs',
        clientName: text(data.clientName) || same?.clientName || 'Client',
        deskDevice: text(data.deskDevice) || same?.deskDevice || null,
        enteredAt: isoOrNull(data.enteredAt),
        host: text(data.host) || same?.host || null,
        identifiedAt: isoOrNull(data.identifiedAt),
        identifiedInTime: data.identifiedInTime === true,
        identifyBy: isoOrNull(data.identifyBy),
        knownAt: same?.knownAt ?? now,
        platform: platformValue(data.platform) || same?.platform || null
    };
    if (same?.pending && !stepRecorded(call, same.pending)) {
        call.pending = same.pending;
    }
    if (same?.error) {
        call.error = same.error;
    }
    return call;
}
/** external_meeting_status_ack: the server recorded a step. Its times replace the known ones. */
export function withMeetingAck(call, ack) {
    if (ack.callId !== call.callId) {
        return call;
    }
    const next = {
        ...call,
        enteredAt: isoField(ack.enteredAt, call.enteredAt),
        identifiedAt: isoField(ack.identifiedAt, call.identifiedAt),
        identifiedInTime: typeof ack.identifiedInTime === 'boolean' ? ack.identifiedInTime : call.identifiedInTime,
        identifyBy: isoField(ack.identifyBy, call.identifyBy),
        platform: platformValue(ack.platform) || call.platform
    };
    if (next.pending && (ack.event === next.pending || stepRecorded(next, next.pending))) {
        delete next.pending;
        delete next.error;
    }
    return next;
}
/** This phone sent a step. */
export function withMeetingStepSent(call, event) {
    const next = { ...call, pending: event };
    delete next.error;
    return next;
}
/** The server refused the step, or did not answer. The interpreter can try again. */
export function withMeetingStepRefused(call, error) {
    const next = { ...call, error };
    delete next.pending;
    return next;
}
/** "Opens on Chrome on macOS" under an outside-meeting offer, when a desk is connected. */
export function meetingOpensOnText(desk) {
    return `Opens on ${desk?.device || 'your web console'}`;
}
function clockTime(iso) {
    const date = new Date(iso);
    return Number.isFinite(date.getTime()) ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
}
function minutesLeft(ms) {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
/**
 * The card's words and allowed steps at `now`. `desk` is the last
 * meeting_desk_status: undefined when the server did not say.
 */
export function meetingCallView(call, desk, now, formatTime = clockTime) {
    const client = call.clientName || 'the client';
    const entered = Boolean(call.enteredAt);
    const identified = Boolean(call.identifiedAt);
    const vrs = call.callType === 'vrs';
    const deskMissing = desk?.connected === false;
    const deadline = call.identifyBy ? Date.parse(call.identifyBy)
        : entered ? Date.parse(call.enteredAt) + IDENTIFY_WINDOW_MS : Number.NaN;
    const late = vrs && entered && (identified ? !call.identifiedInTime : Number.isFinite(deadline) && now > deadline);
    const since = entered ? `In the meeting since ${formatTime(call.enteredAt)}` : '';
    const device = (desk?.connected && desk.device) || call.deskDevice;
    let status;
    let detail;
    if (!entered) {
        status = deskMissing ? 'Not in the meeting yet' : `Opening on ${device || 'your web console'}…`;
    }
    else if (!vrs) {
        status = since;
    }
    else if (identified) {
        status = since;
        detail = late
            ? 'Identified after 5 minutes. The TRS Fund does not pay for this call.'
            : `Identified at ${formatTime(call.identifiedAt)}`;
    }
    else if (late) {
        status = 'Not identified in 5 minutes. The TRS Fund does not pay for this call.';
        detail = since;
    }
    else {
        status = Number.isFinite(deadline)
            ? `Identify ${client} by ${formatTime(deadline)} (${minutesLeft(deadline - now)} left)`
            : `Identify ${client}`;
        detail = since;
    }
    return {
        canEnd: !call.pending,
        canEnter: !entered && !call.pending,
        canIdentify: vrs && entered && !identified && !call.pending,
        ...deskMissing ? { deskNotice: MEETING_DESK_MISSING_TEXT } : {},
        ...detail ? { detail } : {},
        identifyLabel: `${call.clientName || 'Client'} identified`,
        late,
        showEnter: !entered,
        showIdentify: vrs && !identified,
        status,
        title: `${call.clientName || 'Client'} · ${meetingLabel(call.platform)}`
    };
}
